import { useContext, useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Briefcase,
  ChevronDown,
  ClipboardList,
  Link2,
  PencilLine,
  Plus,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { getEventTypeDescription, getEventTypeLabel } from "../shared/utils";
import { LEAD_EVENT_TYPES, type LeadEventType } from "../shared/types";
import {
  BITRIX_CREATE_DEFAULTS,
  BITRIX_EMAIL_VALUE_TYPES,
  BITRIX_PHONE_VALUE_TYPES,
  BITRIX_RENTALS_TYPES,
  BITRIX_UPDATE_DEFAULTS,
  getMappingEvents,
  stripUfCrmPrefix,
  normalizeUfCrmKey,
  type BitrixCustomFieldEntry,
  type BitrixEventMapping,
  type BitrixFieldExpr,
  type BitrixRentalsType,
} from "./types";
import { BitrixCustomFieldsEditor, BitrixExprValueEditor } from "./BitrixCustomFieldsEditor";

const CONTACT_PHONE_PATH_SUGGESTIONS = [
  { path: "lead.phone", description: "Teléfono del prospecto (con +57)" },
  {
    path: "lead.whatsappUserId",
    description: "ID de WhatsApp de Meta (BSUID) — para prospectos sin teléfono",
  },
] as const;

const CONTACT_EMAIL_PATH_SUGGESTIONS = [
  { path: "lead.email", description: "Email del prospecto" },
] as const;

const ADMINISTRATIVO_EVENT_TYPES: LeadEventType[] = [
  "lead.created",
  "lead.first-message-sent",
  "lead.first-message-delivered",
  "lead.first-message-read",
  "lead.first-message-answered",
  "lead.redirected-to-human",
  "lead.inactivated",
  "lead.discarded",
  "lead.property-info-collected",
  "lead.maintenance-requested",
];

const NON_ADMINISTRATIVO_EVENT_TYPES: LeadEventType[] = LEAD_EVENT_TYPES.filter(
  (e) => e !== "lead.property-info-collected" && e !== "lead.maintenance-requested"
);

const EVENTS_BY_RENTALS_TYPE: Record<BitrixRentalsType, LeadEventType[]> = {
  rent: NON_ADMINISTRATIVO_EVENT_TYPES,
  sale: NON_ADMINISTRATIVO_EVENT_TYPES,
  default: NON_ADMINISTRATIVO_EVENT_TYPES,
  general: ADMINISTRATIVO_EVENT_TYPES,
};

type RentalsTypeWithBitrixConfig = {
  id: BitrixRentalsType;
  name: string;
  description: string;
  crmConfiguration?: {
    id: string;
    bitrixEventsMapping: BitrixEventMapping[];
    isFallback?: boolean;
  };
};

type EventDraft = {
  id: string;
  events: LeadEventType[];
  entity: "lead" | "deal";
  createEnabled: boolean;
  updateEnabled: boolean;
  create: {
    title: string;
    comments: string;
    sourceId: string;
    assignedById: BitrixFieldExpr;
    contactPhone: BitrixFieldExpr;
    contactEmail: BitrixFieldExpr;
    fallbackLastName: string;
    phoneValueType: string;
    emailValueType: string;
    contactCustomFields: BitrixCustomFieldEntry[];
    customFields: BitrixCustomFieldEntry[];
    standardFields: BitrixCustomFieldEntry[];
  };
  update: {
    customFields: BitrixCustomFieldEntry[];
    standardFields: BitrixCustomFieldEntry[];
  };
};

let draftIdCounter = 0;
const nextDraftId = (): string => `draft-${Date.now()}-${draftIdCounter++}`;

const recordToEntries = (
  record: Record<string, BitrixFieldExpr> | undefined,
  labels: Record<string, string> | undefined
): BitrixCustomFieldEntry[] => {
  if (!record) return [];
  return Object.entries(record).map(([fullKey, value]) => ({
    key: stripUfCrmPrefix(fullKey),
    value,
    label: labels?.[fullKey] ?? labels?.[stripUfCrmPrefix(fullKey)],
  }));
};

// Same shape as recordToEntries but for raw top-level Bitrix fields (no
// UF_CRM_ prefix to strip).
const recordToRawEntries = (
  record: Record<string, BitrixFieldExpr> | undefined,
  labels: Record<string, string> | undefined
): BitrixCustomFieldEntry[] => {
  if (!record) return [];
  return Object.entries(record).map(([key, value]) => ({
    key,
    value,
    label: labels?.[key],
  }));
};

const coerceLiteral = (raw: string): string | number | null => {
  if (raw === "") return "";
  if (/^-?\d+$/.test(raw)) return parseInt(raw, 10);
  if (/^-?\d+\.\d+$/.test(raw)) return parseFloat(raw);
  return raw;
};

const normalizeBranch = (
  branch: { kind: "literal"; value: unknown } | { kind: "path"; path: string }
): { kind: "literal"; value: unknown } | { kind: "path"; path: string } => {
  if (branch.kind === "literal") {
    const value = branch.value;
    if (typeof value === "string") {
      // An empty if-branch means "don't send anything": evaluates to null at
      // runtime so the field is dropped on update and sent as null on create.
      const coerced = coerceLiteral(value);
      return { kind: "literal", value: coerced === "" ? null : coerced };
    }
    return { kind: "literal", value };
  }
  return branch;
};

const normalizeExprForSave = (expr: BitrixFieldExpr): BitrixFieldExpr => {
  if (expr === null || typeof expr !== "object") {
    if (typeof expr === "string") return coerceLiteral(expr);
    return expr;
  }
  if (expr.mode === "switch") {
    return {
      ...expr,
      cases: expr.cases.map((c) => ({
        match: c.match,
        output: typeof c.output === "string" ? coerceLiteral(c.output) : c.output,
      })),
      default: typeof expr.default === "string" ? coerceLiteral(expr.default) : expr.default,
    };
  }
  if (expr.mode === "if") {
    return {
      ...expr,
      then: normalizeBranch(expr.then) as typeof expr.then,
      else: normalizeBranch(expr.else) as typeof expr.else,
    };
  }
  if (expr.mode === "lookup") {
    return {
      ...expr,
      rows: expr.rows.map((r) => ({
        match: r.match,
        output: typeof r.output === "string" ? coerceLiteral(r.output) : r.output,
      })),
      default: typeof expr.default === "string" ? coerceLiteral(expr.default) : expr.default,
    };
  }
  return expr;
};

const entriesToRecord = (
  entries: BitrixCustomFieldEntry[]
): {
  values: Record<string, BitrixFieldExpr>;
  labels: Record<string, string>;
} => {
  const values: Record<string, BitrixFieldExpr> = {};
  const labels: Record<string, string> = {};
  for (const entry of entries) {
    const fullKey = normalizeUfCrmKey(entry.key);
    if (!fullKey) continue;
    values[fullKey] = normalizeExprForSave(entry.value);
    const trimmedLabel = entry.label?.trim();
    if (trimmedLabel) labels[fullKey] = trimmedLabel;
  }
  return { values, labels };
};

// Raw equivalent — keys are kept verbatim (CATEGORY_ID, TYPE_ID, …) and
// upper-cased so casing is consistent with Bitrix conventions on save.
const rawEntriesToRecord = (
  entries: BitrixCustomFieldEntry[]
): {
  values: Record<string, BitrixFieldExpr>;
  labels: Record<string, string>;
} => {
  const values: Record<string, BitrixFieldExpr> = {};
  const labels: Record<string, string> = {};
  for (const entry of entries) {
    const key = entry.key.trim().toUpperCase();
    if (!key) continue;
    values[key] = normalizeExprForSave(entry.value);
    const trimmedLabel = entry.label?.trim();
    if (trimmedLabel) labels[key] = trimmedLabel;
  }
  return { values, labels };
};

const normalizeAssignedById = (raw: unknown): BitrixFieldExpr => {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "object") return raw as BitrixFieldExpr;
  return raw as BitrixFieldExpr;
};

const normalizeContactExpr = (raw: unknown, defaultPath: string): BitrixFieldExpr => {
  if (raw === undefined || raw === null || raw === "") {
    return { mode: "path", path: defaultPath };
  }
  if (typeof raw === "object") return raw as BitrixFieldExpr;
  return raw as BitrixFieldExpr;
};

const mappingToDraft = (mapping: BitrixEventMapping): EventDraft => ({
  id: nextDraftId(),
  events: getMappingEvents(mapping),
  entity:
    mapping.create?.entity === "deal" || mapping.update?.entity === "deal"
      ? "deal"
      : "lead",
  createEnabled: !!mapping.create,
  updateEnabled: !!mapping.update,
  create: {
    title: mapping.create?.title ?? BITRIX_CREATE_DEFAULTS.title,
    comments: mapping.create?.comments ?? BITRIX_CREATE_DEFAULTS.comments ?? "",
    sourceId: mapping.create?.sourceId ?? "",
    assignedById: normalizeAssignedById(mapping.create?.assignedById),
    contactPhone: normalizeContactExpr(mapping.create?.contactPhone, "lead.phone"),
    contactEmail: normalizeContactExpr(mapping.create?.contactEmail, "lead.email"),
    fallbackLastName:
      mapping.create?.contactDefaults?.fallbackLastName ??
      BITRIX_CREATE_DEFAULTS.contactDefaults?.fallbackLastName ??
      "palomma",
    phoneValueType:
      mapping.create?.contactDefaults?.phoneValueType ??
      BITRIX_CREATE_DEFAULTS.contactDefaults?.phoneValueType ??
      "MOBILE",
    emailValueType:
      mapping.create?.contactDefaults?.emailValueType ??
      BITRIX_CREATE_DEFAULTS.contactDefaults?.emailValueType ??
      "WORK",
    contactCustomFields: recordToEntries(
      mapping.create?.contactCustomFields,
      mapping.create?.contactCustomFieldLabels
    ),
    customFields: recordToEntries(
      mapping.create?.customFields,
      mapping.create?.customFieldLabels
    ),
    standardFields: recordToRawEntries(
      mapping.create?.standardFields,
      mapping.create?.standardFieldLabels
    ),
  },
  update: {
    customFields: recordToEntries(
      mapping.update?.customFields,
      mapping.update?.customFieldLabels
    ),
    standardFields: recordToRawEntries(
      mapping.update?.standardFields,
      mapping.update?.standardFieldLabels
    ),
  },
});

const emptyDraft = (event: LeadEventType): EventDraft =>
  mappingToDraft({
    events: [event],
    create: BITRIX_CREATE_DEFAULTS,
    update: BITRIX_UPDATE_DEFAULTS,
  });

const isEmptyExpr = (expr: BitrixFieldExpr): boolean => {
  if (expr === null || expr === undefined) return true;
  if (typeof expr === "string") return expr.trim() === "";
  return false;
};

const draftToMapping = (draft: EventDraft): BitrixEventMapping => {
  const mapping: BitrixEventMapping = { events: draft.events };
  if (draft.createEnabled) {
    const assignedByIdExpr = isEmptyExpr(draft.create.assignedById)
      ? undefined
      : normalizeExprForSave(draft.create.assignedById);
    const contactPhoneExpr = isEmptyExpr(draft.create.contactPhone)
      ? undefined
      : normalizeExprForSave(draft.create.contactPhone);
    const contactEmailExpr = isEmptyExpr(draft.create.contactEmail)
      ? undefined
      : normalizeExprForSave(draft.create.contactEmail);
    const createContactFields = entriesToRecord(draft.create.contactCustomFields);
    const createFields = entriesToRecord(draft.create.customFields);
    const createStandard = rawEntriesToRecord(draft.create.standardFields);
    mapping.create = {
      entity: draft.entity,
      title: draft.create.title,
      comments: draft.create.comments || undefined,
      sourceId: draft.create.sourceId || undefined,
      assignedById: assignedByIdExpr,
      contactPhone: contactPhoneExpr,
      contactEmail: contactEmailExpr,
      contactDefaults: {
        fallbackLastName: draft.create.fallbackLastName || undefined,
        phoneValueType: draft.create.phoneValueType || undefined,
        emailValueType: draft.create.emailValueType || undefined,
      },
      contactCustomFields:
        Object.keys(createContactFields.values).length > 0
          ? createContactFields.values
          : undefined,
      contactCustomFieldLabels:
        Object.keys(createContactFields.labels).length > 0
          ? createContactFields.labels
          : undefined,
      customFields: createFields.values,
      customFieldLabels:
        Object.keys(createFields.labels).length > 0 ? createFields.labels : undefined,
      standardFields:
        Object.keys(createStandard.values).length > 0 ? createStandard.values : undefined,
      standardFieldLabels:
        Object.keys(createStandard.labels).length > 0 ? createStandard.labels : undefined,
    };
  }
  if (draft.updateEnabled) {
    const updateFields = entriesToRecord(draft.update.customFields);
    const updateStandard = rawEntriesToRecord(draft.update.standardFields);
    mapping.update = {
      entity: draft.entity,
      customFields: updateFields.values,
      customFieldLabels:
        Object.keys(updateFields.labels).length > 0 ? updateFields.labels : undefined,
      standardFields:
        Object.keys(updateStandard.values).length > 0 ? updateStandard.values : undefined,
      standardFieldLabels:
        Object.keys(updateStandard.labels).length > 0 ? updateStandard.labels : undefined,
    };
  }
  return mapping;
};

const FieldLabel = ({
  children,
  hint,
  htmlFor,
}: {
  children: React.ReactNode;
  hint?: React.ReactNode;
  htmlFor?: string;
}): JSX.Element => (
  <div className="mb-1.5 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
    <Label htmlFor={htmlFor} className="text-xs font-medium text-foreground">
      {children}
    </Label>
    {hint && (
      <span className="text-xs leading-tight text-muted-foreground sm:text-right">
        {hint}
      </span>
    )}
  </div>
);

// One Bitrix entity per group. Everything inside a group is sent to that
// entity and nothing else — the header names the entity and the API method so
// a field list deep in the page still reads as "contact" or "lead", not as an
// anonymous "Campos personalizados".
type BitrixGroupEntity = "contact" | "lead" | "deal";

const BITRIX_ENTITY_META: Record<
  BitrixGroupEntity,
  { label: string; Icon: typeof UserRound }
> = {
  contact: { label: "Contacto", Icon: UserRound },
  lead: { label: "Lead", Icon: ClipboardList },
  deal: { label: "Deal", Icon: Briefcase },
};

// Spanish nouns for copy that names the lead/deal entity ("del lead",
// "en Leads"). Kept together so every label uses the same word.
const entityWords = (entity: "lead" | "deal"): { noun: string; plural: string } =>
  entity === "deal" ? { noun: "deal", plural: "Deals" } : { noun: "lead", plural: "Leads" };

const EntityGroup = ({
  entity,
  method,
  description,
  children,
}: {
  entity: BitrixGroupEntity;
  method: string;
  description: string;
  children: React.ReactNode;
}): JSX.Element => {
  const { label, Icon } = BITRIX_ENTITY_META[entity];
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <header className="flex items-start gap-3 border-b border-border bg-muted/40 px-4 py-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-foreground">
          <Icon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 id={headingId} className="text-base font-semibold leading-tight text-foreground">
              {label}
            </h4>
            <code className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {method}
            </code>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>
      </header>
      <div className="divide-y divide-border">{children}</div>
    </section>
  );
};

const GroupSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element => (
  <div className="flex flex-col gap-3 px-4 py-4">
    <div>
      <h5 className="text-sm font-medium text-foreground">{title}</h5>
      {description && (
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      )}
    </div>
    {children}
  </div>
);

// Sits between the contact group and the lead/deal group: the second is
// created after, and linked to, the first.
const GroupConnector = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <p className="flex items-center gap-2 px-4 text-xs text-muted-foreground">
    <Link2 className="size-3.5 shrink-0" aria-hidden />
    <span>{children}</span>
  </p>
);

const EventChip = ({
  eventType,
  onRemove,
}: {
  eventType: LeadEventType;
  onRemove?: () => void;
}): JSX.Element => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-default items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-0.5 text-xs font-medium text-primary">
          {getEventTypeLabel(eventType)}
          {onRemove && (
            <button
              type="button"
              className="relative -mr-0.5 ml-0.5 flex size-4 items-center justify-center rounded-full text-primary/50 hover:bg-primary/10 hover:text-primary before:absolute before:-inset-2 before:content-[''] md:before:hidden"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={`Quitar ${getEventTypeLabel(eventType)}`}
            >
              <X className="size-3" />
            </button>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {getEventTypeDescription(eventType)}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const RentalsTypeCard = ({
  rentalsType,
  isSelected,
  onSelect,
}: {
  rentalsType: RentalsTypeWithBitrixConfig;
  isSelected: boolean;
  onSelect: () => void;
}): JSX.Element => {
  const configured = rentalsType.crmConfiguration?.bitrixEventsMapping || [];
  const isConfigured = configured.length > 0;
  const eventCount = configured.reduce((acc, m) => acc + getMappingEvents(m).length, 0);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group flex flex-col gap-2 rounded-lg border border-border bg-card p-4 text-left transition-all",
        "hover:bg-muted/30",
        isSelected && "ring-2 ring-primary"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-foreground">
              {rentalsType.name}
            </p>
            {rentalsType.crmConfiguration?.isFallback && (
              <Badge variant="outline" className="h-5 shrink-0 border-border bg-muted px-1.5 text-xs font-medium text-muted-foreground">
                heredado
              </Badge>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {rentalsType.description}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          {isConfigured ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              Activo
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              Sin configurar
            </span>
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {isConfigured
            ? `${configured.length} grupo${configured.length === 1 ? "" : "s"} · ${eventCount} evento${eventCount === 1 ? "" : "s"}`
            : "Toca para configurar"}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs font-medium",
            isSelected
              ? "text-primary"
              : "text-primary/70 group-hover:text-primary"
          )}
        >
          {isSelected ? "Editando" : isConfigured ? "Editar" : "Configurar"}
          <PencilLine className="size-3" />
        </span>
      </div>
    </button>
  );
};

const EventGroupSummary = ({
  draft,
}: {
  draft: EventDraft;
}): JSX.Element => {
  const entityLabel = draft.entity === "deal" ? "deal" : "lead";
  const parts: string[] = [];
  if (draft.createEnabled) parts.push(`Al crear ${entityLabel}`);
  if (draft.updateEnabled) parts.push(`Al actualizar ${entityLabel}`);
  const actionSummary = parts.length > 0 ? parts.join(" · ") : "Sin acciones";
  return (
    <div className="flex flex-1 items-center gap-3 pr-3 text-left">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {draft.events.map((e) => (
          <span
            key={e}
            className="inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-xs font-medium text-primary"
          >
            {getEventTypeLabel(e)}
          </span>
        ))}
      </div>
      <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
        {actionSummary}
      </span>
    </div>
  );
};

const EventGroupEditor = ({
  draft,
  availableEventTypes,
  onChange,
  onRemove,
  onAddEvent,
  onRemoveEvent,
}: {
  draft: EventDraft;
  availableEventTypes: LeadEventType[];
  onChange: (patch: (d: EventDraft) => EventDraft) => void;
  onRemove: () => void;
  onAddEvent: (event: LeadEventType) => void;
  onRemoveEvent: (event: LeadEventType) => void;
}): JSX.Element => {
  const { noun: entityNoun, plural: entityPlural } = entityWords(draft.entity);
  return (
    <div className="flex flex-col gap-5 pt-2">
      {/* Event chip row */}
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <FieldLabel hint="Todos los eventos de este grupo comparten la misma configuración.">
          Eventos
        </FieldLabel>
        <div className="flex flex-wrap items-center gap-2">
          {draft.events.map((eventType) => (
            <EventChip
              key={eventType}
              eventType={eventType}
              onRemove={
                draft.events.length > 1 ? () => onRemoveEvent(eventType) : undefined
              }
            />
          ))}
          {availableEventTypes.length > 0 && (
            <Select
              value=""
              onValueChange={(v) => v && onAddEvent(v as LeadEventType)}
            >
              <SelectTrigger className="h-11 w-auto gap-1 border-dashed text-xs md:h-7">
                <Plus className="size-3" />
                <SelectValue placeholder="Agregar evento" />
              </SelectTrigger>
              <SelectContent>
                {availableEventTypes.map((eventType) => (
                  <SelectItem key={eventType} value={eventType} className="text-xs">
                    {getEventTypeLabel(eventType)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <FieldLabel hint="Lead usa crm.lead.add / crm.lead.update. Deal usa crm.deal.add / crm.deal.update y permite STAGE_ID, CATEGORY_ID y TYPE_ID en Campos estándar.">
          Entidad de Bitrix
        </FieldLabel>
        <EntityPicker
          value={draft.entity}
          onChange={(entity) => onChange((d) => ({ ...d, entity }))}
        />
      </div>

      <Tabs defaultValue="create" className="w-full">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList variant="solid" className="w-full sm:w-auto">
            <TabsTrigger value="create" className="flex-1 gap-2 sm:flex-none">
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  draft.createEnabled ? "bg-emerald-500" : "bg-muted-foreground/40"
                )}
              />
              {draft.entity === "deal" ? "Al crear deal" : "Al crear lead"}
            </TabsTrigger>
            <TabsTrigger value="update" className="flex-1 gap-2 sm:flex-none">
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  draft.updateEnabled ? "bg-emerald-500" : "bg-muted-foreground/40"
                )}
              />
              {draft.entity === "deal" ? "Al actualizar deal" : "Al actualizar lead"}
            </TabsTrigger>
          </TabsList>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 gap-1.5 text-muted-foreground hover:text-destructive md:h-8"
            onClick={onRemove}
          >
            <Trash2 className="size-3.5" />
            Eliminar grupo
          </Button>
        </div>

        {/* Create tab */}
        <TabsContent value="create" className="mt-4">
          <ActionHeader
            title={
              draft.entity === "deal" ? "Crear deal en Bitrix" : "Crear lead en Bitrix"
            }
            description={
              draft.entity === "deal"
                ? "Se ejecuta cuando el deal aún no existe en Bitrix24."
                : "Se ejecuta cuando el lead aún no existe en Bitrix24."
            }
            enabled={draft.createEnabled}
            onToggle={(checked) =>
              onChange((d) => ({ ...d, createEnabled: checked }))
            }
          />
          {draft.createEnabled && <CreateLeadEditor draft={draft} onChange={onChange} />}
        </TabsContent>

        {/* Update tab */}
        <TabsContent value="update" className="mt-4">
          <ActionHeader
            title={
              draft.entity === "deal"
                ? "Actualizar deal existente"
                : "Actualizar lead existente"
            }
            description={
              draft.entity === "deal"
                ? "Se ejecuta cuando Palomma ya sincronizó este deal con Bitrix24. Solo envía los campos que elijas."
                : "Se ejecuta cuando Palomma ya sincronizó este lead con Bitrix24. Solo envía los campos que elijas."
            }
            enabled={draft.updateEnabled}
            onToggle={(checked) =>
              onChange((d) => ({ ...d, updateEnabled: checked }))
            }
          />
          {draft.updateEnabled && (
            <div className="mt-4">
              <EntityGroup
                entity={draft.entity}
                method={draft.entity === "deal" ? "crm.deal.update" : "crm.lead.update"}
                description="El contacto no se actualiza: se crea una sola vez, al crear el lead o deal."
              >
                <GroupSection
                  title={`Campos estándar del ${entityNoun}`}
                  description="Campos top-level sin prefijo (CATEGORY_ID, STAGE_ID, …). Solo se envían los que tengan valor."
                >
                  <BitrixCustomFieldsEditor
                    entries={draft.update.standardFields}
                    onChange={(entries) =>
                      onChange((d) => ({
                        ...d,
                        update: { ...d.update, standardFields: entries },
                      }))
                    }
                    emptyLabel={`Sin campos estándar del ${entityNoun} todavía.`}
                    addLabel={`Agregar campo al ${entityNoun}`}
                    keyMode="raw"
                    entity={draft.entity}
                  />
                </GroupSection>
                <GroupSection
                  title={`Campos personalizados del ${entityNoun}`}
                  description={`UF_CRM_* definidos en ${entityPlural} de Bitrix24.`}
                >
                  <BitrixCustomFieldsEditor
                    entries={draft.update.customFields}
                    onChange={(entries) =>
                      onChange((d) => ({
                        ...d,
                        update: { ...d.update, customFields: entries },
                      }))
                    }
                    emptyLabel={`Sin campos personalizados del ${entityNoun} todavía.`}
                    addLabel={`Agregar campo al ${entityNoun}`}
                    entity={draft.entity}
                  />
                </GroupSection>
              </EntityGroup>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const EntityPicker = ({
  value,
  onChange,
}: {
  value: "lead" | "deal";
  onChange: (next: "lead" | "deal") => void;
}): JSX.Element => (
  <Select value={value} onValueChange={(v) => onChange(v as "lead" | "deal")}>
    <SelectTrigger className="h-9 w-full text-sm sm:max-w-xs">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="lead" className="text-sm">
        Lead (crm.lead.add / crm.lead.update)
      </SelectItem>
      <SelectItem value="deal" className="text-sm">
        Deal (crm.deal.add / crm.deal.update)
      </SelectItem>
    </SelectContent>
  </Select>
);

const ActionHeader = ({
  title,
  description,
  enabled,
  onToggle,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
}): JSX.Element => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {enabled ? "Activado" : "Desactivado"}
      </span>
      <Switch checked={enabled} onCheckedChange={onToggle} />
    </div>
  </div>
);

const CreateLeadEditor = ({
  draft,
  onChange,
}: {
  draft: EventDraft;
  onChange: (patch: (d: EventDraft) => EventDraft) => void;
}): JSX.Element => {
  const patchCreate = (
    patch: Partial<EventDraft["create"]> | ((c: EventDraft["create"]) => EventDraft["create"])
  ) =>
    onChange((d) => ({
      ...d,
      create: typeof patch === "function" ? patch(d.create) : { ...d.create, ...patch },
    }));

  const { noun: entityNoun, plural: entityPlural } = entityWords(draft.entity);

  return (
    <div className="mt-4 flex flex-col gap-4">
      <EntityGroup
        entity="contact"
        method="crm.contact.add"
        description="Se crea primero: la persona (nombre, teléfono, email) y los campos personalizados de Contactos."
      >
        <GroupSection title="Nombre, teléfono y email">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <FieldLabel hint="Campo del prospecto que contiene el número.">
                Teléfono del contacto
              </FieldLabel>
              <BitrixExprValueEditor
                value={draft.create.contactPhone}
                onChange={(contactPhone) => patchCreate({ contactPhone })}
                pathSuggestions={CONTACT_PHONE_PATH_SUGGESTIONS}
                pathModeLabel="Teléfono del prospecto"
              />
            </div>
            <div>
              <FieldLabel hint="Si resuelve a vacío, no se envía email a Bitrix.">
                Email del contacto
              </FieldLabel>
              <BitrixExprValueEditor
                value={draft.create.contactEmail}
                onChange={(contactEmail) => patchCreate({ contactEmail })}
                pathSuggestions={CONTACT_EMAIL_PATH_SUGGESTIONS}
                pathModeLabel="Email del prospecto"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <FieldLabel hint="Cuando el prospecto no tiene apellido.">
                Apellido por defecto
              </FieldLabel>
              <Input
                className="h-9 text-sm"
                value={draft.create.fallbackLastName}
                onChange={(e) => patchCreate({ fallbackLastName: e.target.value })}
                placeholder="palomma"
              />
            </div>
            <div>
              <FieldLabel>Tipo de teléfono</FieldLabel>
              <Select
                value={draft.create.phoneValueType}
                onValueChange={(phoneValueType) => patchCreate({ phoneValueType })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BITRIX_PHONE_VALUE_TYPES.map((v) => (
                    <SelectItem key={v} value={v} className="text-sm">
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Tipo de email</FieldLabel>
              <Select
                value={draft.create.emailValueType}
                onValueChange={(emailValueType) => patchCreate({ emailValueType })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BITRIX_EMAIL_VALUE_TYPES.map((v) => (
                    <SelectItem key={v} value={v} className="text-sm">
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </GroupSection>

        <GroupSection
          title="Campos personalizados del contacto"
          description={`UF_CRM_* definidos en Contactos de Bitrix24. Son distintos a los del ${entityNoun}: el campo debe existir en Contactos y tiene su propio número. Solo se envían al crear el contacto.`}
        >
          <BitrixCustomFieldsEditor
            entries={draft.create.contactCustomFields}
            onChange={(entries) =>
              patchCreate((c) => ({ ...c, contactCustomFields: entries }))
            }
            emptyLabel="Sin campos personalizados del contacto todavía."
            addLabel="Agregar campo al contacto"
            entity="contact"
          />
        </GroupSection>
      </EntityGroup>

      <GroupConnector>
        El {entityNoun} se crea después y queda vinculado a este contacto (CONTACT_ID).
      </GroupConnector>

      <EntityGroup
        entity={draft.entity}
        method={draft.entity === "deal" ? "crm.deal.add" : "crm.lead.add"}
        description={
          draft.entity === "deal"
            ? "La oportunidad: título, etapa, responsable y los campos personalizados de Deals."
            : "La oportunidad: título, responsable y los campos personalizados de Leads."
        }
      >
        <GroupSection title="Título y responsable">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <FieldLabel hint="Campo TITLE · obligatorio">
                {draft.entity === "deal" ? "Título del deal" : "Título del lead"}
              </FieldLabel>
              <Input
                className="h-9 text-sm"
                value={draft.create.title}
                onChange={(e) => patchCreate({ title: e.target.value })}
                placeholder="Palomma - {{lead.name}}"
              />
            </div>
            <div>
              <FieldLabel hint="Campo COMMENTS">Comentarios</FieldLabel>
              <Input
                className="h-9 text-sm"
                value={draft.create.comments}
                onChange={(e) => patchCreate({ comments: e.target.value })}
                placeholder="Creado desde Palomma"
              />
            </div>
            <div>
              <FieldLabel hint="Campo SOURCE_ID · opcional">Fuente (source ID)</FieldLabel>
              <Input
                className="h-9 text-sm font-mono"
                value={draft.create.sourceId}
                onChange={(e) => patchCreate({ sourceId: e.target.value })}
                placeholder="UC_SZBGLU"
              />
            </div>
          </div>

          <div>
            <FieldLabel hint="ID numérico del usuario Bitrix">
              Responsable (ASSIGNED_BY_ID)
            </FieldLabel>
            <BitrixExprValueEditor
              value={draft.create.assignedById}
              onChange={(assignedById) => patchCreate({ assignedById })}
            />
          </div>
        </GroupSection>

        <GroupSection
          title={`Campos estándar del ${entityNoun}`}
          description="Campos top-level sin prefijo (CATEGORY_ID, TYPE_ID, STAGE_ID, …). Recomendado para Deals."
        >
          <BitrixCustomFieldsEditor
            entries={draft.create.standardFields}
            onChange={(entries) =>
              patchCreate((c) => ({ ...c, standardFields: entries }))
            }
            emptyLabel={`Sin campos estándar del ${entityNoun} todavía.`}
            addLabel={`Agregar campo al ${entityNoun}`}
            keyMode="raw"
            entity={draft.entity}
          />
        </GroupSection>

        <GroupSection
          title={`Campos personalizados del ${entityNoun}`}
          description={`UF_CRM_* definidos en ${entityPlural} de Bitrix24.`}
        >
          <BitrixCustomFieldsEditor
            entries={draft.create.customFields}
            onChange={(entries) =>
              patchCreate((c) => ({ ...c, customFields: entries }))
            }
            emptyLabel={`Sin campos personalizados del ${entityNoun} todavía.`}
            addLabel={`Agregar campo al ${entityNoun}`}
            entity={draft.entity}
          />
        </GroupSection>
      </EntityGroup>
    </div>
  );
};

const BitrixCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const {
    data: crmConfigurations,
    isLoading: isLoadingConfigurations,
    refetch: refetchConfigurations,
  } = trpc.crmSelfConfig.listCrmConfigurations.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const bitrixConfigurations = useMemo(
    () => (crmConfigurations || []).filter((config) => config.provider === "bitrix"),
    [crmConfigurations]
  );

  const fallbackConfiguration = useMemo(
    () => bitrixConfigurations.find((config) => config.isFallback),
    [bitrixConfigurations]
  );

  const rentalsTypesWithConfig = useMemo<RentalsTypeWithBitrixConfig[]>(() => {
    if (merchantType !== "rentals") return [];
    return BITRIX_RENTALS_TYPES.map((rentalsType) => {
      const config =
        bitrixConfigurations.find((c) => c.rentalsType === rentalsType.id) ||
        fallbackConfiguration;
      return {
        id: rentalsType.id,
        name: rentalsType.name,
        description: rentalsType.description,
        crmConfiguration: config
          ? {
              id: config.id,
              bitrixEventsMapping: (config.bitrixEventsMapping || []) as BitrixEventMapping[],
              isFallback: !config.rentalsType && config.isFallback,
            }
          : undefined,
      };
    });
  }, [bitrixConfigurations, fallbackConfiguration, merchantType]);

  const [editingRentalsType, setEditingRentalsType] = useState<BitrixRentalsType | null>(null);
  const [drafts, setDrafts] = useState<EventDraft[]>([]);
  const [openGroupIds, setOpenGroupIds] = useState<string[]>([]);

  const utils = trpc.useUtils();
  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración guardada");
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("No se pudo guardar", {
        description: error.message || "Ocurrió un error al guardar la configuración.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Cambios guardados");
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("No se pudieron guardar los cambios", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  useEffect(() => {
    if (!editingRentalsType) {
      setDrafts([]);
      setOpenGroupIds([]);
      return;
    }
    const config = rentalsTypesWithConfig.find((r) => r.id === editingRentalsType)?.crmConfiguration;
    if (!config?.bitrixEventsMapping) {
      setDrafts([]);
      setOpenGroupIds([]);
      return;
    }
    const newDrafts = config.bitrixEventsMapping.map(mappingToDraft);
    setDrafts(newDrafts);
    // Open all groups by default when entering edit mode
    setOpenGroupIds(newDrafts.map((d) => d.id));
  }, [editingRentalsType, rentalsTypesWithConfig]);

  const availableEventTypes: LeadEventType[] = useMemo(() => {
    const allowed = editingRentalsType
      ? EVENTS_BY_RENTALS_TYPE[editingRentalsType]
      : LEAD_EVENT_TYPES;
    const used = new Set<LeadEventType>();
    for (const d of drafts) for (const e of d.events) used.add(e);
    return allowed.filter((e) => !used.has(e));
  }, [drafts, editingRentalsType]);

  const handleAddDraft = (event: LeadEventType) => {
    const draft = emptyDraft(event);
    setDrafts((prev) => [...prev, draft]);
    setOpenGroupIds((prev) => [...prev, draft.id]);
  };

  const handleRemoveDraft = (id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    setOpenGroupIds((prev) => prev.filter((gid) => gid !== id));
  };

  const handleAddEventToDraft = (id: string, event: LeadEventType) => {
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, events: [...d.events, event] } : d))
    );
  };

  const handleRemoveEventFromDraft = (id: string, event: LeadEventType) => {
    setDrafts((prev) =>
      prev
        .map((d) => (d.id === id ? { ...d, events: d.events.filter((e) => e !== event) } : d))
        .filter((d) => d.events.length > 0)
    );
  };

  const patchDraft = (id: string, patch: (draft: EventDraft) => EventDraft) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? patch(d) : d)));
  };

  const handleCancel = () => {
    setEditingRentalsType(null);
  };

  const handleSave = async () => {
    if (!merchantId || !editingRentalsType) return;

    for (const draft of drafts) {
      if (draft.events.length === 0) {
        toast.error("Cada grupo debe tener al menos un evento");
        return;
      }
      if (draft.createEnabled && !draft.create.title.trim()) {
        const label = draft.events.map(getEventTypeLabel).join(", ");
        toast.error(
          draft.entity === "deal" ? "Falta el título del deal" : "Falta el título del lead",
          {
            description: `El grupo "${label}" tiene activada la creación pero no tiene título.`,
          }
        );
        return;
      }
    }

    const bitrixEventsMapping = drafts.map(draftToMapping);

    const currentConfiguration = rentalsTypesWithConfig.find(
      (r) => r.id === editingRentalsType
    )?.crmConfiguration;

    if (currentConfiguration && !currentConfiguration.isFallback) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: currentConfiguration.id,
        bitrixEventsMapping,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        provider: "bitrix",
        enabled: true,
        rentalsType: editingRentalsType,
        bitrixEventsMapping,
      });
    }
  };

  if (isLoadingConfigurations) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-sm text-muted-foreground">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  if (!crmConnection || crmConnection.provider !== "bitrix") {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Conecta Bitrix24 arriba para configurar los eventos.
      </div>
    );
  }

  if (merchantType !== "rentals") {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        La configuración de Bitrix24 está disponible solo para comercios de tipo arriendos.
      </div>
    );
  }

  const selectedRentalsType = editingRentalsType
    ? BITRIX_RENTALS_TYPES.find((t) => t.id === editingRentalsType)
    : null;

  const isSaving = createConfiguration.isLoading || updateConfiguration.isLoading;

  return (
    <div className="flex flex-col gap-6">
      {/* Business type picker */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="text-base font-semibold text-foreground">Eventos y mapeo</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Elige qué debe ocurrir en Bitrix24 cuando cambia un lead en Palomma. Palomma decide
          automáticamente si crear o actualizar según si el lead ya fue sincronizado.
        </p>

        <div className="mt-5">
          <FieldLabel>Tipo de negocio</FieldLabel>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {rentalsTypesWithConfig.map((rentalsType) => (
              <RentalsTypeCard
                key={rentalsType.id}
                rentalsType={rentalsType}
                isSelected={editingRentalsType === rentalsType.id}
                onSelect={() => setEditingRentalsType(rentalsType.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Editor */}
      {editingRentalsType && selectedRentalsType ? (
        <div className="rounded-xl border border-border bg-card">
          <header className="flex flex-col gap-3 border-b border-border p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Editando
              </p>
              <h3 className="mt-0.5 text-base font-semibold text-foreground">
                {selectedRentalsType.name}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Spinner size="sm" className="mr-2" />}
                Guardar cambios
              </Button>
            </div>
          </header>

          <div className="flex flex-col gap-4 p-6">
            {drafts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
                <p className="text-sm font-medium text-foreground">
                  Nada configurado todavía
                </p>
                <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                  Los eventos le dicen a Palomma qué hacer en Bitrix24 cuando cambia un
                  prospecto — por ejemplo, crear el lead cuando entra o actualizarlo cuando
                  se agenda una cita. Agrega uno abajo para empezar.
                </p>
              </div>
            ) : (
              <Accordion
                type="multiple"
                value={openGroupIds}
                onValueChange={setOpenGroupIds}
                className="flex flex-col gap-3"
              >
                {drafts.map((draft) => (
                  <AccordionItem
                    key={draft.id}
                    value={draft.id}
                    className="overflow-hidden rounded-lg border border-border bg-card"
                  >
                    <AccordionTrigger className="group px-4 py-3 hover:no-underline data-[state=open]:border-b data-[state=open]:border-border">
                      <EventGroupSummary draft={draft} />
                    </AccordionTrigger>
                    <AccordionContent className="border-t-0 bg-muted/10 px-4 pb-4">
                      <EventGroupEditor
                        draft={draft}
                        availableEventTypes={availableEventTypes}
                        onChange={(patch) => patchDraft(draft.id, patch)}
                        onRemove={() => handleRemoveDraft(draft.id)}
                        onAddEvent={(e) => handleAddEventToDraft(draft.id, e)}
                        onRemoveEvent={(e) => handleRemoveEventFromDraft(draft.id, e)}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}

            {/* Add event group */}
            {availableEventTypes.length > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 p-3">
                <Plus className="size-4 shrink-0 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Agregar evento:</span>
                <Select
                  value=""
                  onValueChange={(v) => v && handleAddDraft(v as LeadEventType)}
                >
                  <SelectTrigger className="h-11 flex-1 text-xs md:h-8 sm:max-w-xs">
                    <SelectValue placeholder="Elegir evento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableEventTypes.map((eventType) => (
                      <SelectItem key={eventType} value={eventType} className="text-xs">
                        <div className="flex flex-col">
                          <span>{getEventTypeLabel(eventType)}</span>
                          <span className="text-xs text-muted-foreground">
                            {getEventTypeDescription(eventType)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center">
          <ChevronDown className="mx-auto size-5 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            Elige un tipo de negocio arriba para configurar sus eventos.
          </p>
        </div>
      )}
    </div>
  );
};

export default BitrixCrmConfiguration;
